import React, { createContext, useContext } from 'react';
import { useJsApiLoader } from '@react-google-maps/api';
import {
  GOOGLE_MAPS_API_KEY,
  GOOGLE_MAPS_LIBRARIES,
  GOOGLE_MAPS_SCRIPT_ID,
  isMapsConfigured,
} from '../config/maps';

/**
 * App-wide Google Maps loader. Wrap the root tree once so the JS API is
 * loaded a single time; child map components then read `isLoaded` from
 * `useMapsLoader()` to decide whether they can render `<GoogleMap>`.
 *
 * If the API key is missing, `isLoaded` stays false forever and child
 * components should render a friendly fallback ("Map unavailable – set
 * REACT_APP_GOOGLE_MAPS_API_KEY"). This prevents a hard crash in dev.
 */

interface MapsContextValue {
  isLoaded: boolean;
  loadError: Error | undefined;
  isConfigured: boolean;
}

const MapsContext = createContext<MapsContextValue>({
  isLoaded: false,
  loadError: undefined,
  isConfigured: false,
});

export const MapsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const configured = isMapsConfigured();

  // The hook is unconditional (rules of hooks). When the key is missing we
  // pass a placeholder so the loader short-circuits without trying to fetch.
  const { isLoaded, loadError } = useJsApiLoader({
    id: GOOGLE_MAPS_SCRIPT_ID,
    googleMapsApiKey: configured ? GOOGLE_MAPS_API_KEY : '',
    libraries: GOOGLE_MAPS_LIBRARIES,
    preventGoogleFontsLoading: true,
  });

  return (
    <MapsContext.Provider
      value={{
        isLoaded: configured && isLoaded,
        loadError,
        isConfigured: configured,
      }}
    >
      {children}
    </MapsContext.Provider>
  );
};

export function useMapsLoader(): MapsContextValue {
  return useContext(MapsContext);
}
